import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

export interface ManualSignInPayload {
  email: string;
  password: string;
}

export interface ManualSignUpPayload {
  body: Record<string, unknown>;
  profileImage?: File | string | null;
}

export const useGoogleLoginMutation = () => {
  return useMutation({
    mutationFn: async (tokenData: any) => {
      // Extract the raw string if it's an object from response
      const val =
        typeof tokenData === 'string'
          ? tokenData
          : tokenData.token || tokenData.code || tokenData.access_token;

      // Some backends check 'token', others 'access_token'. We send both to be safe.
      const payload = {
        token: val,
        access_token: val,
      };

      const { data } = await apiClient.post('/custom_account/continue_with_google/', payload, {
        headers: { Authorization: '' },
      });
      return data;
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Google login failed'));
    },
  });
};

export interface AppleSignInPayload {
  identity_token: string;
  authorization_code: string;
  user_id?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
}

export const useAppleLoginMutation = () => {
  return useMutation({
    mutationFn: async (appleData: AppleSignInPayload) => {
      const { data } = await apiClient.post('/custom_account/continue_with_apple/', appleData, {
        headers: { Authorization: '' }, // Bypass global interceptor for login
      });
      return data;
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Apple login failed'));
    },
  });
};

export const useSendOtpMutation = () => {
  return useMutation({
    mutationFn: async ({
      email,
      type = 'registration',
    }: {
      email: string;
      type?: 'registration' | 'password';
    }) => {
      const { data } = await apiClient.post(
        `/custom_account/send-otp/${type}/`,
        { email },
        {
          headers: { Authorization: '' },
        },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Verification code sent to your email');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to send OTP'));
    },
  });
};

export const useEmailLoginMutation = () => {
  return useMutation({
    mutationFn: async ({ email, otp }: { email: string; otp: string }) => {
      // Documentation says the endpoint is verify-otp and field is otp_code
      const { data } = await apiClient.post(
        '/custom_account/verify-otp/',
        {
          email,
          otp_code: otp,
        },
        {
          headers: { Authorization: '' },
        },
      );
      return data;
    },
  });
};

export const useManualSignInMutation = () => {
  return useMutation({
    mutationFn: async ({ email, password }: ManualSignInPayload) => {
      const { data } = await apiClient.post(
        '/custom_account/manual_login/',
        { email, password },
        {
          headers: { Authorization: '' },
        },
      );

      return data;
    },
    onSuccess: () => {
      toast.success('Login successful');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Login failed'));
    },
  });
};

export const useManualSignUpMutation = () => {
  return useMutation({
    mutationFn: async ({ body, profileImage }: ManualSignUpPayload) => {
      const formData = new FormData();
      formData.append('body', JSON.stringify(body));

      if (profileImage) {
        formData.append('prof_pics', profileImage);
      }

      const { data } = await apiClient.post('/custom_account/signup/', formData, {
        headers: {
          Authorization: '',
          'Content-Type': 'multipart/form-data',
        },
      });

      return data;
    },
    onSuccess: () => {
      toast.success('Registration successful!');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Signup failed'));
    },
  });
};

export const useResetPasswordRequestMutation = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await apiClient.post(
        '/custom_account/send-otp/password/',
        { email },
        {
          headers: { Authorization: '' },
        },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Verification code sent to your email');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Reset request failed'));
    },
  });
};

export const useVerifyResetOtpMutation = () => {
  return useMutation({
    mutationFn: async ({ email, otp }: { email: string; otp: string }) => {
      const { data } = await apiClient.post(
        '/custom_account/verify-otp/',
        {
          email,
          otp_code: otp,
        },
        {
          headers: { Authorization: '' },
        },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Code verified');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Verification failed'));
    },
  });
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: async ({
      email,
      otp,
      newPassword,
    }: {
      email: string;
      otp: string;
      newPassword: string;
    }) => {
      const { data } = await apiClient.post(
        '/custom_account/password-reset/',
        {
          email,
          otp,
          new_password: newPassword,
        },
        {
          headers: { Authorization: '' },
        },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Password reset successful');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Password reset failed'));
    },
  });
};

export const useUpdateProfileMutation = () => {
  return useMutation({
    mutationFn: async (profileData: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { silent, ...backendData } = profileData || {};
      const { data } = await apiClient.patch('/custom_account/update-profile/', backendData);
      return data;
    },
    onSuccess: (data, variables) => {
      if (!variables?.silent) {
        toast.success('Profile updated');
      }
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Update failed'));
    },
  });
};

export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: async ({
      email,
      otp,
      old_password,
      new_password,
    }: {
      email: string;
      otp: string;
      old_password: string;
      new_password: string;
    }) => {
      const { data } = await apiClient.post('/custom_account/change-password/', {
        email,
        otp,
        old_password,
        new_password,
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Password updated successfully');
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to update password'));
    },
  });
};

export const useUserProfileQuery = (userId?: string | null) => {
  return useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await apiClient.get(`/profiles/${userId}/`);
      return data;
    },
    enabled: !!userId,
  });
};

export const useDeleteAccountMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.delete('/custom_account/delete/', {
        data: { confirm_deletion: true },
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Account deleted successfully');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      window.location.href = '/sign-in';
    },
    onError: (err: any) => {
      toast.error(extractApiError(err, 'Failed to delete account'));
    },
  });
};
